import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../features/auth/components/AuthProvider';

export default function ChatInterno({ solicitudId, agenteId, adminId, onClose }) {
  const { user, rol } = useAuth();
  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [loading, setLoading] = useState(true);
  const mensajesEndRef = useRef(null);

  useEffect(() => {
    if (!solicitudId) return;

    // Crear referencia a la subcolección de mensajes
    const mensajesRef = collection(db, 'solicitudes_empresa', solicitudId, 'chat_interno');
    const q = query(mensajesRef, orderBy('fecha', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const mensajesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha?.toDate ? doc.data().fecha.toDate() : new Date(doc.data().fecha)
      }));
      setMensajes(mensajesData);
      setLoading(false);
      
      // Scroll al final
      setTimeout(() => {
        mensajesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }, (error) => {
      console.error('Error escuchando mensajes:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [solicitudId]);

  const enviarMensaje = async (e) => {
    e.preventDefault();
    if (!nuevoMensaje.trim() || enviando) return;

    setEnviando(true);
    try {
      const mensajeData = {
        texto: nuevoMensaje.trim(),
        remitente: {
          id: user.uid,
          nombre: user.displayName || user.email,
          email: user.email,
          rol: rol
        },
        fecha: serverTimestamp(),
        leido: false
      };

      await addDoc(
        collection(db, 'solicitudes_empresa', solicitudId, 'chat_interno'),
        mensajeData
      );

      setNuevoMensaje('');
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      alert('Error al enviar el mensaje. Intenta nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const fechaObj = fecha instanceof Date ? fecha : fecha.toDate();
    const ahora = new Date();
    const diffMs = ahora - fechaObj;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffMins < 1440) return `Hace ${Math.floor(diffMins / 60)} h`;
    
    return fechaObj.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const esMiMensaje = (mensaje) => {
    return mensaje.remitente?.id === user.uid || mensaje.remitente?.email === user.email;
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Chat Interno</h3>
            <p className="text-sm text-gray-600">Comunicación sobre esta solicitud</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mensajes */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '400px' }}>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Cargando mensajes...</p>
          </div>
        ) : mensajes.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No hay mensajes aún. Sé el primero en escribir.</p>
          </div>
        ) : (
          mensajes.map((mensaje) => {
            const esMio = esMiMensaje(mensaje);
            return (
              <div
                key={mensaje.id}
                className={`flex ${esMio ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    esMio
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${
                      esMio ? 'text-blue-100' : 'text-gray-600'
                    }`}>
                      {mensaje.remitente?.nombre || mensaje.remitente?.email}
                    </span>
                    <span className={`text-xs ${
                      esMio ? 'text-blue-200' : 'text-gray-500'
                    }`}>
                      ({mensaje.remitente?.rol || 'usuario'})
                    </span>
                  </div>
                  <p className={`text-sm ${esMio ? 'text-white' : 'text-gray-800'}`}>
                    {mensaje.texto}
                  </p>
                  <p className={`text-xs mt-1 ${
                    esMio ? 'text-blue-200' : 'text-gray-500'
                  }`}>
                    {formatearFecha(mensaje.fecha)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={mensajesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={enviarMensaje} className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <div className="flex gap-2">
          <input
            type="text"
            value={nuevoMensaje}
            onChange={(e) => setNuevoMensaje(e.target.value)}
            placeholder="Escribe un mensaje..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={enviando}
          />
          <button
            type="submit"
            disabled={!nuevoMensaje.trim() || enviando}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {enviando ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
}




